import { Button, Modal } from 'antd'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DownloadOutlined } from '@ant-design/icons'
import { parseDate, startOfMonth, endOfMonth } from '../../../../../config/dayjsConfig'

import { VoucherView } from '../../../../../features/vouchers/components/VoucherTable'
import { VoucherQueryParams } from '../../../vouchers/types'
import { getRequestParams } from '../../../../../features/vouchers/utils/voucherUtils'
import ToolBar from '../../../../../components/Misc/ToolBar'
import { DimensionTree } from '../../../../../features/dimension/components/DimensionTree'
import { useXlsxDownload } from '../../../../../features/vouchers/queries/useXlsxDownload'
import { ExplainerVariable } from '../../../../../redux/context/customReports/typesVariable'
import { CustomColumnContext } from '../grid/types'

interface VoucherXlsxDownloadButtonProps {
  initialParams: Partial<VoucherQueryParams>
  companyId: string
  dimensionQuery?: string
}

const VoucherXlsxDownloadButton: React.FC<VoucherXlsxDownloadButtonProps> = ({
  initialParams,
  companyId,
  dimensionQuery
}) => {
  const { download: downloadVoucherXlsx, isDownloading: fileLoading } = useXlsxDownload(
    getRequestParams(initialParams),
    companyId,
    dimensionQuery
  )

  return (
    <Button type="default" icon={<DownloadOutlined />} loading={fileLoading} onClick={() => downloadVoucherXlsx()}>
      Excel
    </Button>
  )
}

interface CustomReportVoucherModalProps {
  visible: boolean
  row?: ExplainerVariable
  columnContext?: CustomColumnContext
  onCancel: any
}

const CustomReportVoucherModal: React.FC<CustomReportVoucherModalProps> = ({
  row,
  visible,
  columnContext,
  onCancel
}) => {
  const companyId = columnContext?.company?.id ? String(columnContext.company.id) : undefined
  const dimensionId = columnContext?.dimension?.dimensionId
  const startDate = columnContext?.category?.startDate
  const endDate = columnContext?.category?.endDate
  const { t } = useTranslation()

  // Build dimension query from row and column context
  const dimensionQuery = useMemo(() => {
    const rowDimension = row?.params.dimension?.dimensionId
    const dimensionIds = [
      [rowDimension].filter((id): id is string => id !== undefined),
      [dimensionId].filter((id): id is string => id !== undefined)
    ].filter(arr => arr.length > 0)

    const query = DimensionTree.queryBuilder(dimensionIds)
    return query ? JSON.stringify([query]) : undefined
  }, [row?.params.dimension?.dimensionId, dimensionId])

  // Build initial params for VoucherTable
  const initialParams = useMemo((): Partial<VoucherQueryParams> | undefined => {
    if (!companyId || !row?.code) return undefined

    // Validate dates
    if (!startDate || !parseDate(startDate) || !endDate || !parseDate(endDate)) {
      console.warn('Invalid dates provided, cannot load vouchers')
      return undefined
    }

    return {
      accounts: [row.code as number],
      from: startOfMonth(startDate) || undefined,
      until: endOfMonth(endDate)
    }
  }, [companyId, row?.code, startDate, endDate])

  const [exportParams, setExportParams] = useState<Partial<VoucherQueryParams> | undefined>(initialParams)

  return (
    <Modal
      destroyOnHidden
      forceRender={false}
      width="90vw"
      style={{ maxWidth: 1600 }}
      title={`${t('global:vouchers')}  ${row?.name}`}
      open={visible}
      onCancel={onCancel}
      maskClosable={false}
      onOk={onCancel}
      className="voucher-modal"
      footer={null}
    >
      <div style={{ padding: '16px 0' }}>
        <ToolBar justifyContent="right">
          {exportParams && companyId && (
            <VoucherXlsxDownloadButton
              initialParams={exportParams}
              companyId={String(companyId)}
              dimensionQuery={dimensionQuery}
            />
          )}
        </ToolBar>
        {initialParams && (
          <VoucherView
            initialParams={initialParams}
            companyId={companyId}
            dimensions={dimensionQuery}
            height={600}
            onExportParamsChange={setExportParams}
          />
        )}
      </div>
    </Modal>
  )
}

export default CustomReportVoucherModal
